import { useState } from 'react';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Pill, Skeleton, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import type { FinancingTabProps } from '../record/RecordFrame';
import {
  archiveOffer,
  discardOffer,
  useFinancierRealtime,
  useOffers,
  useRefreshFinancing,
  type FinancierRecordFields,
  type OfferRow,
  type OfferUploadRow,
  type OffersView,
} from './api';
import { ChooseOfferDialog } from './ChooseOfferDialog';
import { ManualOfferForm } from './ManualOfferForm';
import { NotFinanceableDialog } from './NotFinanceableDialog';
import { OfferReviewCard } from './OfferReviewCard';
import { OfferDropzone, UploadRows, useOfferPicker, useOfferUploadQueue } from './OfferUpload';
import { RejectionDialog } from './RejectionDialog';
import { RequestNoteDialog } from './RequestNoteDialog';
import {
  AdvisorCard,
  ChooseRuleCard,
  ExpiryRuleCard,
  OpenTasksCard,
  RequestSideCard,
  UnitSuggestionCard,
  UploadHintCard,
} from './sidebars';
import styles from './financier.module.css';

/**
 * Tab „Angebote" (Finanzierer 06/08/10/11): review card, uploaded files with the manual entry, the offer table with
 * „Wählen"/„Neues Angebot hochladen"/„Archivieren", the expiry warning and „Verlängerung", the all-rejected alert;
 * the sidebar follows the frame of the situation.
 */

function OfferTableRow({
  row,
  canEdit,
  archiving,
  onChoose,
  onReplace,
  onArchive,
}: {
  row: OfferRow;
  canEdit: boolean;
  archiving: boolean;
  onChoose: (row: OfferRow) => void;
  onReplace: (row: OfferRow) => void;
  onArchive: (row: OfferRow) => void;
}) {
  const showActions = canEdit && (row.actions.choose || row.actions.replace || row.actions.archive);
  return (
    <div className={clsx(styles.offerRow, row.dimmed && styles.dimmed)} role="row">
      <span role="cell" style={{ minWidth: 0 }}>
        <span className={styles.offerBank}>{row.bank}</span>
        {row.sourceLine ? <span className={styles.offerSource}>{row.sourceLine}</span> : null}
      </span>
      <span role="cell" style={{ display: 'flex' }}>
        <Pill variant={row.tone}>{row.statusLabel}</Pill>
      </span>
      <span role="cell" className={styles.offerLine}>
        {row.line}
      </span>
      <span role="cell" className={styles.offerValid}>
        {row.validLine}
      </span>
      <span role="cell" className={styles.offerActions}>
        {showActions && row.actions.choose ? (
          <Button variant="secondary" size="small" onClick={() => onChoose(row)}>
            Wählen
          </Button>
        ) : null}
        {showActions && row.actions.replace ? (
          <Button variant="secondary" size="small" onClick={() => onReplace(row)}>
            Neues Angebot hochladen
          </Button>
        ) : null}
        {showActions && row.actions.archive ? (
          <button
            type="button"
            className={styles.linkMuted}
            disabled={archiving}
            onClick={() => onArchive(row)}
          >
            Archivieren
          </button>
        ) : null}
      </span>
    </div>
  );
}

type SidebarKind = 'rejected' | 'chosen' | 'uploads' | 'default';

function sidebarKind(view: OffersView, uploading: boolean, manual: boolean): SidebarKind {
  if (view.allRejected) return 'rejected';
  if (view.expiringBanner || view.rows.some((row) => row.status === 'chosen')) return 'chosen';
  if (view.uploads.length > 0 || uploading || manual) return 'uploads';
  return 'default';
}

function OffersSkeleton() {
  return (
    <div className={styles.main} aria-busy="true">
      <Skeleton variant="block" height={180} />
      <Skeleton variant="block" height={120} />
    </div>
  );
}

export function OffersTab(props: FinancingTabProps) {
  const record: FinancierRecordFields = props.financing;
  const offers = useOffers(record.id);
  const refresh = useRefreshFinancing(record.id);
  const { toast } = useToast();
  useFinancierRealtime(record.id);

  const [manualFor, setManualFor] = useState<OfferUploadRow | null>(null);
  const [choosing, setChoosing] = useState<OfferRow | null>(null);
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const [busyOfferId, setBusyOfferId] = useState<string | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [rejectionOpen, setRejectionOpen] = useState(false);
  const [notFinanceableOpen, setNotFinanceableOpen] = useState(false);

  async function changed() {
    await refresh();
    props.onChanged();
  }

  const queue = useOfferUploadQueue(record.id, () => void changed());
  const picker = useOfferPicker(record.id, queue.add);

  const view = offers.data;
  const canEdit = view?.canEdit ?? false;
  const canEditNote = canEdit && record.actions.markRequested.allowed;

  async function archive(row: OfferRow) {
    setArchivingId(row.id);
    try {
      await archiveOffer(record.id, row.id);
      await changed();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setArchivingId(null);
    }
  }

  async function otherFile(upload: OfferUploadRow) {
    setBusyOfferId(upload.offerId);
    try {
      await discardOffer(record.id, upload.offerId);
      if (manualFor?.offerId === upload.offerId) setManualFor(null);
      picker.open(null);
      await changed();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusyOfferId(null);
    }
  }

  const manualUpload =
    manualFor && view ? view.uploads.find((upload) => upload.offerId === manualFor.offerId) : null;
  const uploading = queue.entries.length > 0;
  const hasChosen = view?.rows.some((row) => row.status === 'chosen') ?? false;
  const showUploadsCard = view ? view.uploads.length > 0 || uploading : false;
  /** Finanzierer 10: while only uploads exist, the files and the manual entry stand alone (no empty offer table). */
  const showOffersCard = view ? view.rows.length > 0 || !showUploadsCard : false;
  const kind = view ? sidebarKind(view, uploading, Boolean(manualUpload)) : 'default';

  return (
    <div className={clsx(styles.content, kind === 'rejected' ? styles.contentTall : styles.contentWide)}>
      {picker.input}
      {offers.isPending ? <OffersSkeleton /> : null}
      {offers.isError ? (
        <div className={styles.main}>
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void offers.refetch()}>
                Erneut versuchen
              </Button>
            }
          >
            {errorMessage(offers.error)}
          </Banner>
        </div>
      ) : null}
      {view ? (
        <div className={styles.main}>
          {view.expiringBanner ? (
            <div className={styles.warnLine} role="status">
              <span className={styles.warnTitle}>{view.expiringBanner.title}</span>
              <span className={styles.warnText}>{view.expiringBanner.text}</span>
            </div>
          ) : null}
          {view.review && canEdit ? (
            <OfferReviewCard financingId={record.id} review={view.review} onDone={() => void changed()} />
          ) : null}
          {showUploadsCard ? (
            <section className={styles.card} aria-label="Hochgeladene Dateien">
              <div className={styles.cardTitle}>Hochgeladene Dateien</div>
              <UploadRows
                uploads={view.uploads}
                entries={queue.entries}
                canEdit={canEdit}
                busyOfferId={busyOfferId}
                onManual={(upload) => setManualFor(upload)}
                onOtherFile={(upload) => void otherFile(upload)}
                onRetry={queue.retry}
                onRemove={queue.remove}
              />
            </section>
          ) : null}
          {manualUpload && canEdit ? (
            <ManualOfferForm
              key={manualUpload.offerId}
              financingId={record.id}
              upload={manualUpload}
              onCancel={() => setManualFor(null)}
              onDone={() => {
                setManualFor(null);
                void changed();
              }}
            />
          ) : null}
          {showOffersCard ? (
            <section
              className={clsx(styles.card, kind === 'rejected' && styles.cardLarge)}
              aria-label="Angebote"
            >
              <div className={styles.cardHead}>
                <div className={styles.cardHeadText}>
                  <div className={styles.cardTitle}>
                    {view.review || showUploadsCard ? 'Weitere Angebote' : 'Angebote'}
                  </div>
                  {view.stageHint && !view.allRejected ? (
                    <div className={styles.cardSubtitle}>{view.stageHint}</div>
                  ) : null}
                </div>
                {canEdit ? (
                  <button type="button" className={styles.linkMuted} onClick={() => setRejectionOpen(true)}>
                    Ablehnung erfassen
                  </button>
                ) : null}
                {canEdit && hasChosen ? (
                  <Button variant="secondary" size="small" onClick={() => picker.open(null)}>
                    Angebot hochladen
                  </Button>
                ) : null}
              </div>
              <div className={styles.rows} role="table" aria-label="Angebote">
                {view.rows.length === 0 ? (
                  <div className={styles.offerEmpty}>Noch keine Angebote.</div>
                ) : (
                  view.rows.map((row) => (
                    <OfferTableRow
                      key={row.id}
                      row={row}
                      canEdit={canEdit}
                      archiving={archivingId === row.id}
                      onChoose={setChoosing}
                      onReplace={(target) => picker.open(target.id)}
                      onArchive={(target) => void archive(target)}
                    />
                  ))
                )}
              </div>
              {canEdit && !hasChosen && !view.allRejected ? (
                <OfferDropzone onPick={() => picker.open(null)} onFiles={(files) => queue.add(files, null)} />
              ) : null}
            </section>
          ) : null}
          {view.allRejected ? (
            <section className={styles.alert} role="alert">
              <div className={styles.alertTitle}>{view.allRejected.title}</div>
              <div className={styles.alertText}>{view.allRejected.text}</div>
              {canEdit ? (
                <div className={styles.alertActions}>
                  {canEditNote ? (
                    <Button variant="secondary" onClick={() => setNoteOpen(true)}>
                      Weitere Bank anfragen
                    </Button>
                  ) : null}
                  {record.actions.notFinanceable.allowed ? (
                    <Button onClick={() => setNotFinanceableOpen(true)}>Als nicht finanzierbar melden</Button>
                  ) : null}
                </div>
              ) : null}
            </section>
          ) : null}
          {view.expiringBanner && canEdit ? (
            <section className={styles.card} aria-label="Verlängerung">
              <div className={styles.renewRow}>
                <div className={styles.renewText}>
                  <div className={styles.eyebrow}>Verlängerung</div>
                  <div className={styles.sideText} style={{ marginTop: 8 }}>
                    Fragen Sie die Verlängerung extern an und laden Sie das neue Angebot hoch. Es ersetzt das
                    alte, das alte bleibt im Verlauf.
                  </div>
                </div>
                <Button
                  onClick={() => picker.open(view.rows.find((row) => row.status === 'chosen')?.id ?? null)}
                >
                  Neues Angebot hochladen
                </Button>
              </div>
            </section>
          ) : null}
        </div>
      ) : null}
      <aside className={styles.side}>
        {kind === 'rejected' && view ? (
          <>
            {view.unitSuggestion ? (
              <UnitSuggestionCard
                financingId={record.id}
                suggestion={view.unitSuggestion}
                canEdit={canEdit}
              />
            ) : null}
            <AdvisorCard financing={record} withActions={false} />
          </>
        ) : null}
        {kind === 'chosen' ? (
          <>
            <OpenTasksCard financing={record} />
            <ExpiryRuleCard />
          </>
        ) : null}
        {kind === 'uploads' ? (
          <>
            {view?.request ? <RequestSideCard text={view.request.text} /> : null}
            <UploadHintCard />
          </>
        ) : null}
        {kind === 'default' ? (
          <>
            {view?.request ? (
              <RequestSideCard
                text={view.request.text}
                onEdit={canEditNote ? () => setNoteOpen(true) : undefined}
              />
            ) : null}
            <ChooseRuleCard />
            <OpenTasksCard financing={record} />
          </>
        ) : null}
      </aside>
      <ChooseOfferDialog
        financing={record}
        offer={choosing}
        onClose={() => setChoosing(null)}
        onDone={() => {
          setChoosing(null);
          void changed();
        }}
      />
      <RequestNoteDialog
        open={noteOpen}
        financing={record}
        onClose={() => setNoteOpen(false)}
        onDone={() => {
          setNoteOpen(false);
          void changed();
        }}
      />
      <RejectionDialog
        open={rejectionOpen}
        financing={record}
        onClose={() => setRejectionOpen(false)}
        onDone={() => {
          setRejectionOpen(false);
          void changed();
        }}
      />
      <NotFinanceableDialog
        open={notFinanceableOpen}
        financing={record}
        onClose={() => setNotFinanceableOpen(false)}
        onDone={() => {
          setNotFinanceableOpen(false);
          void changed();
        }}
      />
    </div>
  );
}
